import type { AxiosError, AxiosRequestConfig } from "axios";
import type { AxiosCacheInstance } from "axios-cache-interceptor";

import { useLogger } from "@blackfisk/vue/composables/useLogger";
import * as axios from "axios";
import { buildKeyGenerator, buildMemoryStorage, setupCache } from "axios-cache-interceptor";

import { appVersion } from "@/appVersion";
import router from "@/router";
import { ensureHeader } from "@/services/blackfisk/deviceId";
import { handleUnauthorized } from "@/services/blackfisk/sessionCoordinator";
import { useIdentityStore } from "@/stores/identity";

const logger = useLogger("client-frontend", "http-client");

const environment = import.meta.env.VITE_ENVIRONMENT;
interface RetryConfig extends AxiosRequestConfig {
  retry: number;
  retryDelay: number;
  retryableStatusList: number[];
  /** Internal: whether a cross-tab token-adoption retry already happened (guards the loop). */
  adoptRetried?: boolean;
}
export const retryableConfig: RetryConfig = {
  retry: 3,
  retryDelay: 250,
  retryableStatusList: [503],
};

// * Requests that have already received one refresh-retry (or adopt-retry) in response to a 401.
//   Tracked by request signature at module scope: axios-cache-interceptor clones the config, so a
//   flag carried on the config object is dropped by the time a retried request 401s again. This Set
//   survives the clone and terminates the 401 → refresh → retry loop on the SECOND 401.
const authRetriedRequests = new Set<string>();
// * Include the request BODY in the key: two parallel same-URL requests with different payloads
//   (e.g. concurrent bulk mutations) must not collide — otherwise the second would see the first's
//   key and be rejected without its refresh-retry. Params + a stable body string.
const requestRetryKey = (config: AxiosRequestConfig | undefined): string => {
  const body = config?.data;
  const bodyStr =
    body === undefined ? "" : typeof body === "string" ? body : JSON.stringify(body ?? {});
  return `${config?.method ?? "get"}:${config?.url ?? ""}:${JSON.stringify(config?.params ?? {})}:${bodyStr}`;
};

// * Session-collapse redirect to /login. Only redirect when the CURRENT route actually requires
//   auth — public routes (/ , /about) must stay put (the boot refresh 401s with no cookie, and
//   bouncing a logged-out visitor to /login is a regression). Local state is still collapsed by
//   the caller; the route guard re-checks on the next protected navigation.
const redirectToLoginIfProtected = (): void => {
  const current = router.currentRoute.value;
  if (current.meta?.requiresAuth === false) {
    return;
  }
  router.push({
    name: "login",
    query: {
      redirect: current.fullPath ?? "/",
    },
  });
};

// * Re-issue a request with a fresh bearer token after a 401. Sets the Authorization (+ CSRF for
//   mutating) headers, disables further retries, and on success releases the in-flight retry key.
//   On a terminal NON-401 retry error the key is also released (it is not a stale-token signal, so
//   a later legitimate 401 of this signature must be able to refresh again); on a repeated 401 the
//   key is left for the outer guard to consume. Shared by the refresh-retry and token-adopt paths.
const retryWithToken = async (
  config: RetryConfig,
  accessToken: string,
  csrfToken: string,
  retryKey: string,
  opts: { adoptRetried?: boolean } = {},
): Promise<unknown> => {
  const retryConfig = { ...config } as RetryConfig;
  if (opts.adoptRetried) {
    retryConfig.adoptRetried = true;
  }
  retryConfig.headers = retryConfig.headers || {};
  retryConfig.headers["Authorization"] = `Bearer ${accessToken}`;
  const isMutating = ["post", "put", "patch", "delete"].includes(
    (retryConfig.method ?? "get").toLowerCase(),
  );
  if (isMutating) {
    retryConfig.headers["X-CSRF-Token"] = csrfToken;
  }
  retryConfig.retry = 0;
  try {
    const retried = await BlackfiskClient.request(retryConfig);
    authRetriedRequests.delete(retryKey);
    return retried;
  } catch (retryError) {
    if ((retryError as Error & { response?: { status?: number } })?.response?.status !== 401) {
      authRetriedRequests.delete(retryKey);
    }
    throw retryError;
  }
};

export class BlackfiskRequestError extends Error {
  // * HTTP status when the error came from a server response (null for network/
  //   cancellation/request-construction errors). Consumers can branch on status
  //   (e.g. 401/403 = definitive auth failure, 429/5xx = transient) without re-parsing messages.
  status: number | null;
  constructor(message: string, status: number | null = null) {
    super(message);
    this.name = "BlackfiskRequestError";
    this.status = status;
  }
}

export const BlackfiskClient: AxiosCacheInstance = setupCache(
  axios.create({
    baseURL: import.meta.env.VITE_BFSK_API_BASE_URL || "/api",
    timeout: 2 * 60 * 1000, // 2 minutes
    withCredentials: true,
    headers: {
      "Content-Type": "application/json",
    },
    ...retryableConfig,
  }),
  {
    storage: buildMemoryStorage(),
    methods: ["get", "post"],
    interpretHeader: true,
    generateKey: buildKeyGenerator((request) => {
      const IdentityStore = useIdentityStore();
      return {
        method: request.method,
        baseURL: request.baseURL,
        data: request.data,
        params: request.params,
        url: request.url,
        accountId: IdentityStore.accountId,
        userId: IdentityStore.userId,
      };
    }),
    ...(environment === "local" && {
      debug: (msg) => logger.debug(String(msg)),
    }),
  },
);

// ! Intercept Request
BlackfiskClient.interceptors.request.use((config) => {
  const IdentityStore = useIdentityStore();

  if (IdentityStore.accountId) {
    config.headers["X-Account-Id"] = IdentityStore.accountId;
  }
  if (IdentityStore.userId) {
    config.headers["X-User-Id"] = IdentityStore.userId;
  }
  if (IdentityStore.accessToken) {
    config.headers["Authorization"] = `Bearer ${IdentityStore.accessToken}`;
  }
  const isMutating = ["post", "put", "patch", "delete"].includes(
    (config.method ?? "get").toLowerCase(),
  );
  if (isMutating && IdentityStore.csrfToken) {
    config.headers["X-CSRF-Token"] = IdentityStore.csrfToken;
  }

  // * Device binding: the session-bearing auth endpoints (refresh/profile) carry the
  //   server-minted device id when one has been persisted, so the backend can bind the
  //   session to the device. Absent from storage (never logged in) → send nothing.
  const requestUrl = config.url ?? "";
  if (requestUrl.includes("/v2/auth/refresh") || requestUrl.includes("/v2/auth/profile")) {
    ensureHeader(config);
  }

  // Don't cache POST requests by default — only cache if explicitly opted in
  if (config.method?.toLowerCase() === "post" && !config.cache) {
    config.cache = false;
  }

  // * Inject AppVersion
  config.params = config.params || {};
  config.params["_v"] = appVersion;
  return config;
});

// ! Intercept Response
BlackfiskClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const IdentityStore = useIdentityStore();
    if (error.response) {
      const axiosConfig = error.config as RetryConfig;
      const serverResponse = error.response.data as BlackfiskResponse;
      const status = error.response.status;
      // # Server Responded
      // * Handle Unauthorized & Retryable
      if (status === 401) {
        const url = axiosConfig?.url ?? "";
        const retryKey = requestRetryKey(axiosConfig);
        // * All 401 policy (endpoint classification, collapse-vs-race-vs-transient, adopt, retry)
        //   lives in sessionPolicy/sessionCoordinator; this interceptor only executes the outcome.
        const outcome = await handleUnauthorized(IdentityStore, {
          url,
          retryKeySeen: authRetriedRequests.has(retryKey),
          adoptRetried: Boolean(axiosConfig?.adoptRetried),
        });
        if (outcome.action === "retry") {
          // * Mark the signature retried BEFORE the retry dispatches so a second 401 for the same
          //   signature terminates (already-retried) instead of looping.
          authRetriedRequests.add(retryKey);
          return await retryWithToken(
            axiosConfig,
            outcome.accessToken,
            outcome.csrfToken,
            retryKey,
            { adoptRetried: outcome.adopt },
          );
        }
        if (outcome.action === "reject") {
          authRetriedRequests.delete(retryKey);
          if (outcome.redirectToLogin) {
            redirectToLoginIfProtected();
          }
          return Promise.reject(new BlackfiskRequestError(outcome.message, outcome.status));
        }
        // * pass-through (credential endpoint): fall through to normalize + return error.response.
      } else {
        logger.error("Request failed: {error}", { error });
      }
      if (!axiosConfig || !axiosConfig.retry) {
        return Promise.reject(new BlackfiskRequestError("Configuration mismatch."));
      }
      if ((axiosConfig?.retryableStatusList ?? [503]).includes(error?.response?.status)) {
        axiosConfig.retry -= 1;
        const delayRetryRequest = new Promise<void>((resolve) => {
          setTimeout(() => {
            resolve();
          }, axiosConfig.retryDelay || 1000);
        });
        return delayRetryRequest.then(() => BlackfiskClient.request(axiosConfig));
      }
      // * Normalize data
      if (serverResponse?.data === null) {
        serverResponse.data = [];
      }
      return error.response;
    } else if (error.code === "ERR_CANCELED") {
      return Promise.reject(new BlackfiskRequestError("Request cancelled."));
    } else if (error.code === "ECONNABORTED") {
      return Promise.reject(new BlackfiskRequestError("Request timed out."));
    } else if (error.request) {
      // # No Response
      return Promise.reject(new BlackfiskRequestError("Server cannot be reached."));
    } else {
      // Handle case where signal is used to abort the call
      return Promise.reject(new BlackfiskRequestError("Error constructing request."));
    }
  },
);

export interface BlackfiskResponse<T = string[]> {
  apiVersion: string;
  code: number;
  data: T;
  error: string[];
  execTimeMS: number;
  message: string[];
  timestamp: number;
}
export const handleErrors = (
  error: AxiosError,
): {
  message: string;
  code: number | string;
  reference: null;
}[] => {
  const responseData = error?.response?.data as BlackfiskResponse;
  if (responseData.error) {
    return responseData.error.map((errorMessage) => ({
      message: errorMessage,
      code: responseData.code,
      reference: null,
    }));
  }
  return [
    {
      message: "UNKNOWN",
      code: "An unknown error occurred.",
      reference: null,
    },
  ];
};
